import { ComponentProps, FC, MouseEventHandler } from "react";
import styles from "./index.module.scss";
import useCashRegisterStore from "@/stores/cash-register";
import { getImageSrc } from "@/helpers/images";
import { logFirebaseEvent } from "@/services/firebase";
import { Routes } from "@/types/routes";
import { playSound, SoundOption } from "@/helpers/playSound";
import useNavigateToStart from "@/hooks/useNavigateToStart";
import { Button } from "@/components/button";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface LogoProps {
  className?: string;
  returnUrlButtonColor?: ComponentProps<typeof Button>["color"];
  returnUrlButtonTextColor?: ComponentProps<typeof Button>["textColor"];
}

const Logo: FC<LogoProps> = ({ returnUrlButtonColor, returnUrlButtonTextColor, className }) => {
  const { t } = useTranslation();
  const navigateToStart = useNavigateToStart();
  const { current } = useCashRegisterStore();
  const returnUrl = sessionStorage.getItem("returnUrl");

  const logoUrl = getImageSrc({
    imageCdnUrl: current?.shop.property.logoCdnUrl,
    imageUrl: current?.shop.property.logoUrl,
  });

  const onReturnUrlPress: MouseEventHandler<HTMLButtonElement> = (event) => {
    // Prevent the click from bubbling up to the other elements
    event.stopPropagation();
    playSound(SoundOption.Click2);
    navigateToStart();
  };

  const onLogoPress = () => {
    logFirebaseEvent("logo_pressed");
    if (window.location.pathname.includes(Routes.MAIN)) {
      playSound(SoundOption.Click2);
      navigateToStart();
    }
  };

  if (returnUrl)
    return (
      <Button
        onClick={onReturnUrlPress}
        text={`< ${t("ui.back")}`}
        cta={false}
        size="x-small"
        data-cy="return-url-home"
        className={className}
        color={returnUrlButtonColor}
        textColor={returnUrlButtonTextColor}
      />
    );

  return logoUrl.includes("no-image") ? (
    <div
      className={classNames(styles.logoReplacement, className)}
      onClick={onLogoPress}
      data-cy="topbar-logo-fallback">
      <h2 className={styles.shopName}>{current?.shop.name}</h2>
      <h4 className={styles.propertyName}>{current?.shop.property.name}</h4>
    </div>
  ) : (
    <img
      src={logoUrl}
      className={classNames(styles.logo, className)}
      alt="Logo"
      onClick={onLogoPress}
      data-cy="topbar-logo"
    />
  );
};

export default Logo;
